import { useMemo, useState } from 'react';
import { usePageMeta } from '@/hooks/usePageMeta';
import { videos } from '@/mocks/videos';
import Breadcrumb from '@/components/feature/Breadcrumb';
import VideoCard from '@/components/feature/VideoCard';
import Pagination from '@/components/feature/Pagination';
import { videoTypeOptions } from '@/utils/content';

const container = 'w-full max-w-[1280px] mx-auto px-4 md:px-8';
const PAGE_SIZE = 6;

export default function VideosPage() {
  const [filter, setFilter] = useState('all');
  const [sort, setSort] = useState<'latest' | 'hot'>('latest');
  const [page, setPage] = useState(1);

  const filtered = useMemo(() => {
    const list = videos.filter((v) => filter === 'all' || v.type === filter);
    return [...list].sort((a, b) =>
      sort === 'hot' ? b.views - a.views : a.uploadDate < b.uploadDate ? 1 : -1
    );
  }, [filter, sort]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const current = Math.min(page, totalPages);
  const paged = filtered.slice((current - 1) * PAGE_SIZE, current * PAGE_SIZE);

  usePageMeta(
    '精彩视频集 - 91.con娱乐视频',
    '91.con 视频专区汇集影视剧、综艺与短视频资源，分类筛选与排序一应俱全，发现你感兴趣的精彩内容。',
    '娱乐视频,91.con,影视剧,综艺,短视频,视频资源'
  );

  return (
    <>
      <section className="w-full bg-background-100 border-b border-background-200">
        <div className={`${container} py-8 md:py-10`}>
          <Breadcrumb items={[{ label: '视频资源' }]} />
          <h1 className="font-heading text-2xl md:text-4xl font-extrabold text-foreground-950 mt-4">精彩视频集</h1>
          <p className="text-sm md:text-base text-foreground-600 mt-3 max-w-3xl leading-relaxed">
            汇集影视剧、综艺与短视频的精选内容，支持按类型筛选与热度排序。视频播放为前端演示，点击卡片可查看详情。
          </p>
        </div>
      </section>

      <section className={`${container} py-10 md:py-12`}>
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-5 pb-6 border-b border-background-200">
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-sm text-foreground-500 mr-1">类型：</span>
            {videoTypeOptions.map((opt) => (
              <button
                key={opt.key}
                type="button"
                onClick={() => { setFilter(opt.key); setPage(1); }}
                className={`px-4 py-2 rounded-full text-sm transition-colors cursor-pointer whitespace-nowrap border ${
                  filter === opt.key
                    ? 'bg-primary-500 text-background-50 border-primary-500'
                    : 'bg-background-100 border-background-200 text-foreground-700 hover:border-primary-500/60'
                }`}
              >
                {opt.name}
              </button>
            ))}
          </div>

          <div className="flex items-center gap-2">
            <span className="text-sm text-foreground-500 mr-1">排序：</span>
            <div className="flex items-center gap-1 p-1 rounded-full bg-background-100 border border-background-200">
              <button
                type="button"
                onClick={() => { setSort('latest'); setPage(1); }}
                className={`px-4 py-1.5 rounded-full text-sm transition-colors cursor-pointer whitespace-nowrap ${
                  sort === 'latest' ? 'bg-accent-500 text-background-50' : 'text-foreground-700 hover:text-foreground-950'
                }`}
              >
                最新
              </button>
              <button
                type="button"
                onClick={() => { setSort('hot'); setPage(1); }}
                className={`px-4 py-1.5 rounded-full text-sm transition-colors cursor-pointer whitespace-nowrap ${
                  sort === 'hot' ? 'bg-accent-500 text-background-50' : 'text-foreground-700 hover:text-foreground-950'
                }`}
              >
                最热
              </button>
            </div>
          </div>
        </div>

        <p className="text-sm text-foreground-500 mt-6">
          共 <span className="text-primary-400 font-semibold">{filtered.length}</span> 个视频
        </p>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6 mt-5">
          {paged.map((item) => (
            <VideoCard key={item.id} item={item} />
          ))}
        </div>

        <Pagination current={current} total={totalPages} onChange={setPage} />
      </section>
    </>
  );
}